import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Upload, X, AlertCircle, CheckCircle2, FileSpreadsheet, Undo2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/format';
import { decodeCsvBuffer } from '../utils/csvUtils';
import {
  lerTabela, temCabecalho, sugerirMapeamento, sugerirSinal, montarItens,
  lembrarMapeamento, recuperarMapeamento,
} from '../utils/faturaCsv';

const MAX_ITENS = 2000;
const mesAtual = () => new Date().toISOString().slice(0, 7);
const rotuloMes = (ref) => {
  try {
    const [a, m] = ref.split('-');
    const n = new Date(Number(a), Number(m) - 1, 1).toLocaleDateString('pt-BR', { month: 'long' });
    return `${n.charAt(0).toUpperCase()}${n.slice(1)} / ${a}`;
  } catch { return ref; }
};

const CAMPOS = [
  { id: 'data', rotulo: 'Data da compra', obrigatorio: true },
  { id: 'descricao', rotulo: 'Descrição', obrigatorio: true },
  { id: 'valor', rotulo: 'Valor', obrigatorio: true },
  { id: 'categoria', rotulo: 'Categoria (opcional)', obrigatorio: false },
];

const selectCls = 'w-full text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-2 outline-none focus:border-indigo-400';

/**
 * Importa as compras de uma fatura de cartão a partir do CSV do banco.
 * Etapas: arquivo → colunas → prévia (com duplicadas) → resultado (com "desfazer").
 */
export default function ImportarFaturaModal({ open, cartao, referenciaInicial, onClose, onImported }) {
  const [etapa, setEtapa] = useState('arquivo');
  const [referencia, setReferencia] = useState(referenciaInicial || mesAtual());
  const [tabela, setTabela] = useState(null);           // { todas: string[][], nome }
  const [comCabecalho, setComCabecalho] = useState(true);
  const [mapa, setMapa] = useState({ data: -1, descricao: -1, valor: -1, categoria: -1 });
  const [sinal, setSinal] = useState('positivo');
  const [categorias, setCategorias] = useState([]);
  const [categoriaPadrao, setCategoriaPadrao] = useState('');
  const [montado, setMontado] = useState(null);         // { itens, ignoradas, erros }
  const [previa, setPrevia] = useState(null);           // resposta do dryRun
  const [excluidos, setExcluidos] = useState(() => new Set());
  const [permitirDuplicadas, setPermitirDuplicadas] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const inputRef = useRef(null);

  const cabecalho = useMemo(() => (tabela && comCabecalho ? tabela.todas[0] : []), [tabela, comCabecalho]);
  const corpo = useMemo(() => (tabela ? (comCabecalho ? tabela.todas.slice(1) : tabela.todas) : []), [tabela, comCabecalho]);

  useEffect(() => {
    if (!open) return;
    setReferencia(referenciaInicial || mesAtual());
    api.get('/categorias').then((r) => setCategorias(Array.isArray(r.data) ? r.data : [])).catch(() => setCategorias([]));
  }, [open, referenciaInicial]);

  if (!open || !cartao) return null;

  const reiniciar = () => {
    setEtapa('arquivo'); setTabela(null); setMontado(null); setPrevia(null);
    setExcluidos(new Set()); setPermitirDuplicadas(false); setResultado(null); setErro('');
  };
  const fechar = () => { reiniciar(); onClose(); };

  const aplicarSugestao = (todas, cab) => {
    const corpoLocal = cab ? todas.slice(1) : todas;
    const lembrado = cab ? recuperarMapeamento(cartao.id, todas[0]) : null;
    if (lembrado) { setMapa(lembrado.mapa); setSinal(lembrado.sinal); return; }
    const m = sugerirMapeamento(cab ? todas[0] : [], corpoLocal.slice(0, 30));
    setMapa(m);
    setSinal(sugerirSinal(corpoLocal, m.valor));
  };

  const lerArquivo = (f) => {
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) { setErro('Envie o arquivo CSV da fatura (extensão .csv).'); return; }
    setErro('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const { linhas } = lerTabela(decodeCsvBuffer(e.target.result));
      if (linhas.length < 1 || linhas[0].length < 2) { setErro('Não consegui ler colunas neste arquivo. Confira se é um CSV.'); return; }
      if (linhas.length > MAX_ITENS + 1) { setErro(`O arquivo tem mais de ${MAX_ITENS} linhas. Divida a fatura em partes menores.`); return; }
      const cab = temCabecalho(linhas[0]);
      setTabela({ todas: linhas, nome: f.name });
      setComCabecalho(cab);
      aplicarSugestao(linhas, cab);
      setEtapa('colunas');
    };
    reader.readAsArrayBuffer(f);
  };

  const alternarCabecalho = (v) => {
    setComCabecalho(v);
    if (tabela) aplicarSugestao(tabela.todas, v);
  };

  const faltando = CAMPOS.filter((c) => c.obrigatorio && mapa[c.id] < 0).map((c) => c.rotulo);

  const verPrevia = async () => {
    setErro('');
    const res = montarItens(corpo, mapa, { cabecalho: comCabecalho, sinal, categorias });
    if (!res.itens.length) {
      setErro(res.erros.length
        ? `Nenhuma compra válida. Exemplo: linha ${res.erros[0].linha} — ${res.erros[0].erro}. Confira as colunas escolhidas.`
        : 'Nenhuma compra encontrada com essas colunas. Confira o mapeamento e o sinal dos valores.');
      return;
    }
    setCarregando(true);
    try {
      const { data } = await api.post(`/cartoes/${cartao.id}/faturas/${referencia}/importar`, {
        itens: res.itens.map(({ linha, ...i }) => i), dryRun: true, categoriaPadraoId: categoriaPadrao || null,
      });
      lembrarMapeamento(cartao.id, cabecalho, mapa, sinal);
      setMontado(res);
      setPrevia(data);
      setExcluidos(new Set());
      setEtapa('previa');
    } catch (e) {
      setErro(e.response?.data?.detail || 'Não consegui conferir o arquivo. Tente novamente.');
    } finally { setCarregando(false); }
  };

  const statusLinha = (i) => previa?.linhas?.[i]?.status || 'nova';
  const incluidas = montado ? montado.itens.map((it, i) => ({ it, i })).filter(({ i }) => {
    if (excluidos.has(i)) return false;
    return statusLinha(i) === 'nova' || (statusLinha(i) === 'duplicada' && permitirDuplicadas);
  }) : [];
  const valorIncluidas = incluidas.reduce((s, { it }) => s + it.valor, 0);
  const totalArquivo = montado ? montado.itens.reduce((s, it) => s + it.valor, 0) : 0;
  const nDuplicadas = previa?.duplicadas || 0;

  const importar = async () => {
    if (!incluidas.length) return;
    setCarregando(true); setErro('');
    try {
      const { data } = await api.post(`/cartoes/${cartao.id}/faturas/${referencia}/importar`, {
        itens: incluidas.map(({ it: { linha, ...i } }) => i),
        permitirDuplicadas, categoriaPadraoId: categoriaPadrao || null,
      });
      setResultado(data); setEtapa('resultado'); onImported?.();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao importar a fatura.');
    } finally { setCarregando(false); }
  };

  const desfazer = async () => {
    if (!resultado?.importacaoId) return;
    setCarregando(true);
    try {
      const { data } = await api.delete(`/cartoes/${cartao.id}/importacoes/${resultado.importacaoId}`);
      toast.success(`Importação desfeita: ${data.removidas} compra(s) removida(s).`);
      onImported?.();
      fechar();
    } catch { setErro('Não consegui desfazer. Tente novamente.'); } finally { setCarregando(false); }
  };

  const amostra = tabela ? tabela.todas.slice(0, comCabecalho ? 4 : 3) : [];
  const nCols = tabela ? Math.max(...tabela.todas.slice(0, 20).map((l) => l.length)) : 0;
  const opcoesColuna = Array.from({ length: nCols }, (_, i) => ({
    i, rotulo: `Coluna ${i + 1}${comCabecalho && tabela?.todas[0]?.[i] ? ` — ${tabela.todas[0][i]}` : ''}`,
  }));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 nexus-modal-overlay" data-testid="importar-fatura-modal">
      <div className="nexus-modal w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet size={18} className="text-indigo-500 flex-shrink-0" />
            <h2 className="font-bold text-slate-800 dark:text-slate-100 truncate">Importar fatura — {cartao.nome}</h2>
          </div>
          <button onClick={fechar} className="text-slate-400 hover:text-slate-600" aria-label="Fechar"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {erro && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /><span>{erro}</span>
            </div>
          )}

          {etapa === 'arquivo' && (
            <>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mês da fatura</label>
                <input type="month" value={referencia} onChange={(e) => e.target.value && setReferencia(e.target.value)} className={`${selectCls} mt-1 max-w-[220px]`} data-testid="fatura-mes" />
                <p className="text-xs text-slate-400 mt-1">As compras do arquivo entram na fatura de <strong>{rotuloMes(referencia)}</strong>, em aberto.</p>
              </div>
              <div
                onDrop={(e) => { e.preventDefault(); lerArquivo(e.dataTransfer.files[0]); }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-2xl p-10 text-center cursor-pointer hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
              >
                <Upload size={30} className="mx-auto mb-3 text-slate-300" />
                <p className="font-semibold text-slate-600 dark:text-slate-300">Clique ou arraste o CSV da fatura</p>
                <p className="text-sm text-slate-400 mt-1">Baixe o CSV no app ou site do seu banco. Você confere tudo antes de importar.</p>
                <input ref={inputRef} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => lerArquivo(e.target.files[0])} data-testid="fatura-arquivo" />
              </div>
            </>
          )}

          {etapa === 'colunas' && tabela && (
            <>
              <div className="flex items-start gap-2 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl text-sm text-indigo-800 dark:text-indigo-200">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <span>Cada banco organiza o CSV de um jeito. Confirme qual coluna é a data, a descrição e o valor — eu já tentei adivinhar e vou lembrar para a próxima fatura deste cartão.</span>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input type="checkbox" checked={comCabecalho} onChange={(e) => alternarCabecalho(e.target.checked)} />
                A primeira linha do arquivo é o cabeçalho (nomes das colunas)
              </label>

              <div className="overflow-x-auto border border-slate-100 dark:border-slate-700 rounded-xl">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                    {amostra.map((l, r) => (
                      <tr key={r} className={r === 0 && comCabecalho ? 'bg-slate-50 dark:bg-slate-700/50 font-bold' : ''}>
                        {Array.from({ length: nCols }, (_, c) => (
                          <td key={c} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 whitespace-nowrap max-w-[180px] truncate">{l[c] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {CAMPOS.map((c) => (
                  <div key={c.id}>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{c.rotulo}</label>
                    <select value={mapa[c.id]} onChange={(e) => setMapa((p) => ({ ...p, [c.id]: Number(e.target.value) }))} className={`${selectCls} mt-1`} data-testid={`fatura-col-${c.id}`}>
                      <option value={-1}>{c.obrigatorio ? 'Escolha a coluna…' : 'Não usar'}</option>
                      {opcoesColuna.map((o) => <option key={o.i} value={o.i}>{o.rotulo}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nas compras, o valor aparece…</label>
                  <select value={sinal} onChange={(e) => setSinal(e.target.value)} className={`${selectCls} mt-1`} data-testid="fatura-sinal">
                    <option value="positivo">Positivo (pagamentos e estornos são negativos)</option>
                    <option value="negativo">Negativo (pagamentos e estornos são positivos)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria padrão</label>
                  <select value={categoriaPadrao} onChange={(e) => setCategoriaPadrao(e.target.value)} className={`${selectCls} mt-1`}>
                    <option value="">Automática (Cartão de Crédito)</option>
                    {categorias.filter((c) => c.tipo === 'DESPESA').map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {etapa === 'previa' && montado && previa && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-emerald-600">{incluidas.length}</p>
                  <p className="text-xs text-emerald-600 font-medium">A importar · {formatCurrency(valorIncluidas)}</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${nDuplicadas ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-slate-700/50'}`}>
                  <p className={`text-xl font-bold ${nDuplicadas ? 'text-amber-600' : 'text-slate-400'}`}>{nDuplicadas}</p>
                  <p className={`text-xs font-medium ${nDuplicadas ? 'text-amber-600' : 'text-slate-400'}`}>Já existem na fatura</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-slate-500">{montado.ignoradas.length}</p>
                  <p className="text-xs text-slate-400 font-medium">Ignoradas (pagamento/estorno)</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${montado.erros.length ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-50 dark:bg-slate-700/50'}`}>
                  <p className={`text-xl font-bold ${montado.erros.length ? 'text-red-600' : 'text-slate-400'}`}>{montado.erros.length}</p>
                  <p className={`text-xs font-medium ${montado.erros.length ? 'text-red-500' : 'text-slate-400'}`}>Linhas com erro</p>
                </div>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Soma das compras lidas no arquivo: <strong>{formatCurrency(totalArquivo)}</strong> — confira com o total da fatura no seu banco.
              </p>

              {nDuplicadas > 0 && (
                <label className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                  <input type="checkbox" checked={permitirDuplicadas} onChange={(e) => setPermitirDuplicadas(e.target.checked)} />
                  Importar também as {nDuplicadas} compra(s) que já existem nesta fatura
                </label>
              )}

              <div className="border border-slate-100 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="overflow-auto max-h-72">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white dark:bg-slate-800">
                      <tr className="border-b border-slate-100 dark:border-slate-700 text-slate-400">
                        <th className="px-3 py-2 w-8" />
                        <th className="text-left px-3 py-2 font-bold">Data</th>
                        <th className="text-left px-3 py-2 font-bold">Descrição</th>
                        <th className="text-right px-3 py-2 font-bold">Valor</th>
                        <th className="text-left px-3 py-2 font-bold">Situação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                      {montado.itens.map((it, i) => {
                        const st = statusLinha(i);
                        const vaiImportar = !excluidos.has(i) && (st === 'nova' || (st === 'duplicada' && permitirDuplicadas));
                        const pode = st === 'nova' || (st === 'duplicada' && permitirDuplicadas);
                        return (
                          <tr key={i} className={vaiImportar ? '' : 'opacity-50'}>
                            <td className="px-3 py-1.5">
                              <input type="checkbox" checked={vaiImportar} disabled={!pode} aria-label={`Incluir ${it.descricao}`}
                                onChange={() => setExcluidos((p) => { const n = new Set(p); if (n.has(i)) n.delete(i); else n.add(i); return n; })} />
                            </td>
                            <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDate(it.data)}</td>
                            <td className="px-3 py-1.5 text-slate-700 dark:text-slate-200 max-w-[260px] truncate" title={it.descricao}>
                              {it.descricao}{it.parcelaTotal ? <span className="ml-1 text-indigo-500">({it.parcelaAtual}/{it.parcelaTotal})</span> : null}
                            </td>
                            <td className="px-3 py-1.5 text-right font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">{formatCurrency(it.valor)}</td>
                            <td className="px-3 py-1.5">
                              {st === 'duplicada'
                                ? <span className="text-amber-600 font-semibold">{permitirDuplicadas ? 'Duplicada (incluída)' : 'Já existe'}</span>
                                : <span className="text-emerald-600 font-semibold">Nova</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {(montado.ignoradas.length > 0 || montado.erros.length > 0) && (
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer font-semibold">Ver linhas ignoradas e com erro</summary>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {montado.ignoradas.map((x) => <p key={`i${x.linha}`}>Linha {x.linha}: {x.descricao} — ignorada ({x.motivo})</p>)}
                    {montado.erros.map((x) => <p key={`e${x.linha}`} className="text-red-600">Linha {x.linha}: {x.erro}</p>)}
                  </div>
                </details>
              )}
            </>
          )}

          {etapa === 'resultado' && resultado && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={32} className="text-emerald-600" /></div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">Fatura importada!</h3>
              <p className="text-sm text-slate-500">
                <strong className="text-emerald-600">{resultado.criados}</strong> compra{resultado.criados !== 1 ? 's' : ''} em <strong>{rotuloMes(referencia)}</strong> · {formatCurrency(resultado.valorNovas)}
              </p>
              {resultado.duplicadas > 0 && <p className="text-xs text-amber-600 mt-1">{resultado.duplicadas} já existia(m) e foi(ram) ignorada(s).</p>}
              <p className="text-xs text-slate-400 mt-3">As compras entraram em aberto e já contam no limite do cartão.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-between gap-3 flex-shrink-0">
          {etapa === 'arquivo' && <button onClick={fechar} className="fin-btn-secondary ml-auto">Cancelar</button>}
          {etapa === 'colunas' && (
            <>
              <button onClick={reiniciar} className="fin-btn-secondary">← Outro arquivo</button>
              <button onClick={verPrevia} disabled={carregando || faltando.length > 0} className="fin-btn-primary" data-testid="fatura-ver-previa"
                title={faltando.length ? `Falta escolher: ${faltando.join(', ')}` : ''}>
                {carregando ? 'Conferindo…' : 'Ver prévia →'}
              </button>
            </>
          )}
          {etapa === 'previa' && (
            <>
              <button onClick={() => setEtapa('colunas')} className="fin-btn-secondary">← Voltar</button>
              <button onClick={importar} disabled={carregando || !incluidas.length} className="fin-btn-primary" data-testid="fatura-importar">
                {carregando ? 'Importando…' : `Importar ${incluidas.length} compra${incluidas.length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {etapa === 'resultado' && (
            <>
              <button onClick={desfazer} disabled={carregando || !resultado?.importacaoId} className="fin-btn-secondary flex items-center gap-1.5" data-testid="fatura-desfazer">
                <Undo2 size={14} /> Desfazer importação
              </button>
              <button onClick={fechar} className="fin-btn-primary">Fechar</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
